import React, {FC, useContext, useEffect, useState} from "react";
import {
	Box,
	Typography,
	LinearProgress,
	Grid,
	useTheme,
	Breadcrumbs,
	Link,
	Skeleton,
	Button
} from "@mui/material";
import {useParams} from "react-router-dom";
import {ICourseEntity} from "@buildwitt/shapeshift-types/entities";
import {ModuleCard} from "@/pages/RxdCoursePage/ModuleCard";
import bgImage from "@/icons/Rusticibg.png";
import {palette} from "@/constants";
import SummarizeOutlined from "@mui/icons-material/SummarizeOutlined";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {ExpandableDescription} from "@/components/ExpandableDescription";
import {INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";
import useSnackbarManager from "@/snackbars/useSnackbarManager";
import {useRXDStateContext} from "@/contexts/rxdProgressContext";
import {setScore, setPassed, concedeControl} from "@/utils/rxd-api";

export const LandingPage: FC = () => {
	const theme = useTheme();
	const [course, setCourse] = useState<ICourseEntity | null>(null);
	const [modules, setModules] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const {courseSlug} = useParams();
	const mq = useMq();
	const {notify} = useContext(NotificationContext);
	useSnackbarManager();
	
	// RXD Progress Context
	const {
		rxdState,
		lessonMode,
		isNormalMode,
		isModuleCompleted,
		loadSuspendData
	} = useRXDStateContext();
	
	const totalModules = modules.length;
	
	// Calculate progress based on RXD state (0-based index)
	// modulePosition = 0 means no modules completed
	// modulePosition = 1 means first module completed
	// modulePosition = 2 means first two modules completed
	const completedModules = rxdState ? rxdState.modulePosition : 0;
	const progress = totalModules > 0 ? (completedModules / totalModules) * 100 : 0;
	
	// Check if all modules are completed (only in normal mode)
	// All modules completed when modulePosition equals totalModules
	const allModulesCompleted = isNormalMode() && rxdState && rxdState.modulePosition >= totalModules;

	// Complete course function
	const completeCourse = async () => {
		try {
			// Set score to 100
			setScore(100);
			
			// Mark as passed
			setPassed();
			
			// Concede control back to LMS
			concedeControl();
		} catch (error) {
			console.error('Error completing course:', error);
			notify(
				"Error completing course",
				"Please try again or contact support",
				INotificationSeverity.error,
				5000
			);
		}
	};

	// Load suspend data on mount
	useEffect(() => {
		loadSuspendData();
	}, [loadSuspendData]);

	useEffect(() => {
		if (!courseSlug) return;

		const fetchCourse = async () => {
			try {
				const res = await fetch(
					`${process.env.REACT_APP_API_HOST}/api/rxd/courses/${courseSlug}/curriculum`
				);

				if (!res.ok) {
					if (res.status === 404) {
						notify(
							"Course not found error",
							"",
							INotificationSeverity.error,
							5000
						);
					} else {
						notify(
							"No course access",
							"",
							INotificationSeverity.error,
							5000
						);
					}
					return;
				}

				const data = await res.json();
				setCourse(data.course);
				setModules(data.modules || []);
			} catch (err) {
				notify(
					"An unexpected error occurred",
					"",
					INotificationSeverity.error,
					5000
				);
			} finally {
				setLoading(false);
			}
		};

		fetchCourse();
	}, [courseSlug]);

	return (
		<Box
			sx={{
				backgroundImage: `url(${bgImage})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundColor: palette.RxdBackground,
				minHeight: "100vh",
				padding: "32px"
			}}
		>
			<Breadcrumbs aria-label="breadcrumb" sx={{color: palette.RxdBreadcrumbText}}>
				{loading ? (
					<Skeleton variant="text" width={200} height={24} />
				) : (
					<Link
						underline="none"
						color="inherit"
						sx={{display: "flex", alignItems: "center"}}
					>
						<SummarizeOutlined sx={{marginRight: "10px"}} fontSize="inherit" />
						{course?.title}
					</Link>
				)}
			</Breadcrumbs>

			{loading ? (
				<Skeleton variant="text" height={48} width="50%" sx={{marginTop: "30px", marginBottom: "10px"}} />
			) : (
				<Typography
					variant="h4"
					sx={{
						marginTop: "30px",
						marginBottom: "10px",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis"
					}}
					color={theme.palette.primary.contrastText}
				>
					{course?.title}
				</Typography>
			)}

			{loading ? (
				<Skeleton variant="rectangular" height={mq.mobile ? 76 : 26} sx={{marginBottom: "8px"}} />
			) : (
				<ExpandableDescription description={course?.description} />
			)}

			<Box sx={{marginTop: "40px"}}>
				{loading ? (
					<>
						<Skeleton variant="text" width={160} height={20} />
						<Skeleton variant="rectangular" height={8} width="100%" sx={{borderRadius: "10px", marginTop: "10px"}} />
					</>
				) : (
					<>
						<Typography variant="overline" color={palette.RxdBreadcrumbText}>
							{completedModules}/{totalModules} LESSONS COMPLETED
						</Typography>
						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{
								height: "8px",
								borderRadius: "10px",
								marginTop: "10px",
								backgroundColor: palette.RxdProgressTrack,
								"& .MuiLinearProgress-bar": {
									backgroundColor: palette.orange
								}
							}}
						/>
					</>
				)}
			</Box>

			<Grid container spacing={2} sx={{marginTop: "16px"}}>
				{loading
					? Array.from({length: 6}).map((_, i) => (
						<Grid item xs={12} sm={6} md={4} key={i}>
							<Skeleton variant="rectangular" height={200} sx={{borderRadius: "8px"}} />
						</Grid>
					))
					: modules.map((module, index) => {
						// Determine if module is completed based on RXD state
						// modulePosition = 0: no modules completed
						// modulePosition = 1: first module (index 0) completed
						// modulePosition = 2: first two modules (index 0,1) completed
						const isCompleted = rxdState ? rxdState.modulePosition > index : false;
						
						// Determine if module is accessible based on lesson mode and progression
						// In normal mode: first module always accessible, others need previous completed
						// In browse/review: all modules accessible
						const isAccessible = !isNormalMode() || index === 0 || (rxdState && rxdState.modulePosition > index - 1);
						
						return (
							<Grid item xs={12} sm={6} md={4} key={module.id || module.title}>
								<ModuleCard 
									module={module} 
									course={course} 
									lessonNumber={index} 
									completed={isCompleted}
									accessible={isAccessible}
									modules={modules}
								/>
							</Grid>
						);
					})}
			</Grid>
			
			{/* Complete Course Button - only show when all modules completed in normal mode */}
			{allModulesCompleted && (
				<Box sx={{marginTop: "32px", textAlign: "center"}}>
					<Button
						variant="contained"
						size="large"
						sx={{
							backgroundColor: palette.orange,
							color: palette.white,
							padding: "12px 32px",
							fontSize: "16px",
							fontWeight: "bold",
							borderRadius: "8px",
							"&:hover": {
								backgroundColor: palette.orange,
								opacity: 0.9
							}
						}}
						onClick={completeCourse}
					>
						Complete Course
					</Button>
				</Box>
			)}
		</Box>
	);
};
